import { useEffect, useState } from 'react'
import { callOutcomesApi, type CallOutcome, type CreateCallOutcomeRequest } from '../api/leads'
import {
  Page,
  PageHeader,
  Button,
  IconButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Checkbox,
  Icon,
  FormSection,
} from '../components/ui'

interface FormState extends CreateCallOutcomeRequest {
  id?: number
}

const EMPTY_FORM: FormState = {
  code: '',
  label: '',
  isContactUseful: false,
  closesSequence: false,
  sortOrder: 0,
  isActive: true,
}

export function CallOutcomesPage() {
  const [outcomes, setOutcomes] = useState<CallOutcome[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await callOutcomesApi.list(false)
      setOutcomes(data.sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  function openCreateModal() {
    setForm(EMPTY_FORM)
    setModalError(null)
    setShowModal(true)
  }

  function openEditModal(outcome: CallOutcome) {
    setForm({
      id: outcome.id,
      code: outcome.code,
      label: outcome.label,
      isContactUseful: outcome.isContactUseful,
      closesSequence: outcome.closesSequence,
      sortOrder: outcome.sortOrder,
      isActive: outcome.isActive,
    })
    setModalError(null)
    setShowModal(true)
  }

  function closeModal() {
    if (saving) return
    setShowModal(false)
    setModalError(null)
  }

  async function handleSave() {
    if (!form.code.trim() || !form.label.trim()) {
      setModalError('Codice e Etichetta sono obbligatori')
      return
    }

    setSaving(true)
    setModalError(null)

    try {
      const req: CreateCallOutcomeRequest = {
        code: form.code.trim(),
        label: form.label.trim(),
        isContactUseful: form.isContactUseful,
        closesSequence: form.closesSequence,
        sortOrder: form.sortOrder,
        isActive: form.isActive ?? true,
      }

      if (form.id) {
        await callOutcomesApi.update(form.id, req)
      } else {
        await callOutcomesApi.create(req)
      }

      setShowModal(false)
      await load()
    } catch (e) {
      setModalError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Page>
        <LoadingState />
      </Page>
    )
  }

  return (
    <Page>
      {error ? (
        <>
          <PageHeader title="Esiti chiamata" icon="phone_missed" back={{ to: '/settings', label: 'Impostazioni generali' }} />
          <Callout tone="error">{error}</Callout>
        </>
      ) : (
        <>
          <PageHeader
            title="Esiti chiamata"
            icon="phone_missed"
            back={{ to: '/settings', label: 'Impostazioni generali' }}
            subtitle="Configura i possibili esiti di una chiamata."
            actions={
              <Button variant="filled" icon="add" onClick={openCreateModal}>
                Nuovo esito
              </Button>
            }
          />

          <Card variant="outlined" padding="none">
            {outcomes.length === 0 ? (
              <EmptyState icon="phone_missed" title="Nessun esito configurato." />
            ) : (
              <DataTable variant="plain">
                <thead>
                  <tr>
                    <th>Ordine</th>
                    <th>Codice</th>
                    <th>Etichetta</th>
                    <th>Utile</th>
                    <th>Chiude Sequenza</th>
                    <th>Stato</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {outcomes.map(o => (
                    <tr key={o.id}>
                      <td className="md-num">{o.sortOrder}</td>
                      <td className="md-typescale-emphasized-body-medium">{o.code}</td>
                      <td>{o.label}</td>
                      <td>
                        <Icon name={o.isContactUseful ? 'check_circle' : 'cancel'} size={20} />
                        <span className="md-sr-only">{o.isContactUseful ? 'Sì' : 'No'}</span>
                      </td>
                      <td>
                        <Icon name={o.closesSequence ? 'check_circle' : 'cancel'} size={20} />
                        <span className="md-sr-only">{o.closesSequence ? 'Sì' : 'No'}</span>
                      </td>
                      <td>
                        <StatusPill tone={o.isActive ? 'success' : 'neutral'}>
                          {o.isActive ? 'Attivo' : 'Disattivo'}
                        </StatusPill>
                      </td>
                      <td>
                        <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEditModal(o)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
            )}
          </Card>
        </>
      )}

      <Dialog as="form"
 onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        open={showModal}
        onClose={closeModal}
        dismissible={!saving}
        title={form.id ? 'Modifica esito' : 'Nuovo esito'}
        actions={
          <>
            <Button variant="text" onClick={closeModal} disabled={saving}>
              Annulla
            </Button>
            <Button
              type="submit"
              variant="text"
              loading={saving}
              disabled={saving || !form.code.trim() || !form.label.trim()}
            >
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        <>
          {modalError && <Callout tone="error">{modalError}</Callout>}
          <FormSection title="Esito">
            <TextField
              id="co-code"
              label="Codice *"
              type="text"
              value={form.code}
              onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
              disabled={saving}
            />
            <TextField
              id="co-label"
              label="Etichetta *"
              type="text"
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              disabled={saving}
            />
            <TextField
              id="co-sortOrder"
              label="Ordine"
              type="number"
              value={form.sortOrder}
              onChange={e => setForm(f => ({ ...f, sortOrder: parseInt(e.target.value, 10) || 0 }))}
              disabled={saving}
            />
          </FormSection>
          <FormSection title="Opzioni" columns={1}>
            <Checkbox
              id="co-useful"
              label="Contatto Utile (la persona ha mostrato interesse)"
              checked={form.isContactUseful}
              onChange={e => setForm(f => ({ ...f, isContactUseful: e.target.checked }))}
              disabled={saving}
            />
            <Checkbox
              id="co-closes"
              label="Chiude Sequenza (non richiamare più)"
              checked={form.closesSequence}
              onChange={e => setForm(f => ({ ...f, closesSequence: e.target.checked }))}
              disabled={saving}
            />
            {form.id && (
              <Checkbox
                id="co-active"
                label="Attivo (disponibile per la selezione)"
                checked={form.isActive ?? true}
                onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                disabled={saving}
              />
            )}
          </FormSection>
        </>
      </Dialog>
    </Page>
  )
}
